import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Mail, User, Phone, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';

const SubscriptionPopup = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        // Honeypot fields - hidden from users
        website: '',
        url: '',
        honeypot: ''
    });

    const [status, setStatus] = useState({
        type: '', // 'success', 'error', 'loading'
        message: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateEmail = (email) => {
        // Basic format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return 'Please enter a valid email address';
        }

        // Check for disposable email domains
        const disposableDomains = [
            'tempmail.com', 'guerrillamail.com', '10minutemail.com',
            'throwaway.email', 'mailinator.com', 'trashmail.com'
        ];
        const domain = email.split('@')[1]?.toLowerCase();
        if (disposableDomains.includes(domain)) {
            return 'Please use a permanent email address';
        }

        // Check for suspicious patterns (5+ consecutive numbers)
        if (/\d{5,}/.test(email)) {
            return 'Please enter a valid email address';
        }

        return null;
    };

    const validatePhone = (phone) => {
        if (!phone) return null; // Phone is optional

        // Remove spaces and dashes for validation
        const cleanPhone = phone.replace(/[\s-]/g, '');

        // Check if it's a valid phone number (10-15 digits)
        if (!/^\+?\d{10,15}$/.test(cleanPhone)) {
            return 'Please enter a valid phone number';
        }

        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Honeypot detection - if any honeypot field is filled, it's a bot
        if (formData.website || formData.url || formData.honeypot) {
            // Silently pretend success to fool bots
            setStatus({
                type: 'success',
                message: 'Thank you for subscribing! Check your email for confirmation.'
            });
            setTimeout(() => {
                onClose();
            }, 2000);
            return;
        }

        // Validate required fields
        if (!formData.name.trim()) {
            setStatus({
                type: 'error',
                message: 'Please enter your name'
            });
            return;
        }

        if (!formData.email.trim()) {
            setStatus({
                type: 'error',
                message: 'Please enter your email address'
            });
            return;
        }

        // Validate email
        const emailError = validateEmail(formData.email);
        if (emailError) {
            setStatus({
                type: 'error',
                message: emailError
            });
            return;
        }

        // Validate phone if provided
        const phoneError = validatePhone(formData.phone);
        if (phoneError) {
            setStatus({
                type: 'error',
                message: phoneError
            });
            return;
        }

        // Submit to API
        setStatus({ type: 'loading', message: '' });

        try {
            const backendUrl = 'https://admin.goodikids.com';

            const response = await fetch(`${backendUrl}/api/v1/subscribers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email.trim().toLowerCase(),
                    name: formData.name.trim(),
                    phone: formData.phone.trim() || null,
                    source: 'goodikids_website'
                })
            });

            const data = await response.json();

            if (response.ok) {
                // Success or welcome back message
                setStatus({
                    type: 'success',
                    message: data.message || 'Thank you for subscribing! Check your email for confirmation.'
                });

                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    website: '',
                    url: '',
                    honeypot: ''
                });

                // Close popup after 2 seconds
                setTimeout(() => {
                    onClose();
                    setStatus({ type: '', message: '' });
                }, 2000);
            } else if (response.status === 429) {
                // Rate limit exceeded
                setStatus({
                    type: 'error',
                    message: 'Too many attempts. Please try again in a few minutes.'
                });
            } else {
                // Other errors
                setStatus({
                    type: 'error',
                    message: data.message || 'Something went wrong. Please try again.'
                });
            }
        } catch (error) {
            console.error('Subscription error:', error);
            setStatus({
                type: 'error',
                message: 'Network error. Please check your connection and try again.'
            });
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
                    />

                    {/* Popup */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="w-full max-w-md"
                        >
                            <Card className="relative bg-white rounded-3xl shadow-2xl p-8">
                                {/* Close Button */}
                                <button
                                    onClick={onClose}
                                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                                    aria-label="Close"
                                >
                                    <X className="w-6 h-6" />
                                </button>

                                {/* Header */}
                                <div className="text-center mb-6">
                                    <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-yellow-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <Mail className="w-8 h-8 text-white" />
                                    </div>
                                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                        Coming <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Soon!</span>
                                    </h2>
                                    <p className="text-gray-600">
                                        Once we are live on Play Store, we will let you know. Get connected for latest offers and news!
                                    </p>
                                </div>

                                {/* Form */}
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* Name Field */}
                                    <div>
                                        <Label htmlFor="name" className="text-gray-700 font-medium mb-2 block">
                                            Name *
                                        </Label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                            <Input
                                                id="name"
                                                name="name"
                                                type="text"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Enter your name"
                                                className="pl-10 h-12 border-2 border-gray-200 focus:border-orange-500 rounded-xl"
                                                disabled={status.type === 'loading'}
                                            />
                                        </div>
                                    </div>

                                    {/* Email Field */}
                                    <div>
                                        <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">
                                            Email *
                                        </Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="your@email.com"
                                                className="pl-10 h-12 border-2 border-gray-200 focus:border-orange-500 rounded-xl"
                                                disabled={status.type === 'loading'}
                                            />
                                        </div>
                                    </div>

                                    {/* Phone Field */}
                                    <div>
                                        <Label htmlFor="phone" className="text-gray-700 font-medium mb-2 block">
                                            Phone (Optional)
                                        </Label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                            <Input
                                                id="phone"
                                                name="phone"
                                                type="tel"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="+1 234 567 8900"
                                                className="pl-10 h-12 border-2 border-gray-200 focus:border-orange-500 rounded-xl"
                                                disabled={status.type === 'loading'}
                                            />
                                        </div>
                                    </div>

                                    {/* Honeypot Fields - Hidden from users */}
                                    <input
                                        type="text"
                                        name="website"
                                        value={formData.website}
                                        onChange={handleChange}
                                        style={{ display: 'none' }}
                                        tabIndex="-1"
                                        autoComplete="off"
                                        aria-hidden="true"
                                    />
                                    <input
                                        type="text"
                                        name="url"
                                        value={formData.url}
                                        onChange={handleChange}
                                        style={{ display: 'none' }}
                                        tabIndex="-1"
                                        autoComplete="off"
                                        aria-hidden="true"
                                    />
                                    <input
                                        type="text"
                                        name="honeypot"
                                        value={formData.honeypot}
                                        onChange={handleChange}
                                        style={{ display: 'none' }}
                                        tabIndex="-1"
                                        autoComplete="off"
                                        aria-hidden="true"
                                    />

                                    {/* Status Messages */}
                                    {status.message && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className={`flex items-center gap-2 p-3 rounded-xl ${status.type === 'success'
                                                ? 'bg-green-50 text-green-700'
                                                : status.type === 'error'
                                                    ? 'bg-red-50 text-red-700'
                                                    : 'bg-blue-50 text-blue-700'
                                                }`}
                                        >
                                            {status.type === 'success' && <CheckCircle className="w-5 h-5" />}
                                            {status.type === 'error' && <AlertCircle className="w-5 h-5" />}
                                            <span className="text-sm font-medium">{status.message}</span>
                                        </motion.div>
                                    )}

                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        disabled={status.type === 'loading'}
                                        className="w-full h-12 bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-semibold rounded-xl text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {status.type === 'loading' ? (
                                            <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                Subscribing...
                                            </>
                                        ) : (
                                            'Subscribe Now'
                                        )}
                                    </Button>

                                    {/* Privacy Note */}
                                    <p className="text-xs text-gray-500 text-center">
                                        We respect your privacy. Unsubscribe anytime.
                                    </p>
                                </form>
                            </Card>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

export default SubscriptionPopup;
